import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { eventsApi, tournamentApi, assetUrl } from '../utils/api';
import DOMPurify from 'dompurify';
import { eventStatusLabel } from '../utils/eventStatus';

export default function EventOverview() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Registration state
  const [myRegistration, setMyRegistration] = useState(null);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [regStep, setRegStep] = useState('idle'); // 'idle' | 'waiver' | 'done'
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    Promise.all([
      eventsApi.getEvent(eventId),
      tournamentApi.getTournaments(eventId)
    ])
      .then(([eventRes, tournRes]) => {
        setEvent(eventRes.data.data);
        setTournaments(tournRes.data.data || []);
      })
      .catch(err => console.error('Failed to fetch event:', err))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      eventsApi.getMyRegistration(eventId)
        .then(res => setMyRegistration(res.data.data))
        .catch(() => {});
    }
  }, [eventId]);

  if (loading) return <div className="spinner" />;
  if (!event) {
    return (
      <div className="page">
        <div className="page-content" style={{ textAlign: 'center', padding: '60px 0' }}>
          <h2 style={{ color: 'var(--text-secondary)' }}>Event not found</h2>
          <Link to="/events" className="btn btn-ghost" style={{ marginTop: 16 }}>Back to All Events</Link>
        </div>
      </div>
    );
  }

  const startDate = new Date(event.start_date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const endDate = new Date(event.end_date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const isCompleted = event.status === 'completed';
  const isLive = event.status === 'live';
  const eventDatePassed = new Date(event.end_date) < new Date();
  const canRegister = event.status === 'active' && !eventDatePassed;

  async function handleRegister() {
    setRegError('');
    if ((event.waiver_text || event.terms_and_conditions) && regStep === 'idle') {
      setRegStep('waiver');
      return;
    }
    setRegLoading(true);
    try {
      await eventsApi.register(eventId, {});
      if (event.waiver_text || event.terms_and_conditions) {
        await eventsApi.signWaiver(eventId, {
          waiver_signed: waiverAccepted || !event.waiver_text,
          terms_accepted: termsAccepted || !event.terms_and_conditions
        });
      }
      const res = await eventsApi.getMyRegistration(eventId);
      setMyRegistration(res.data.data);
      setRegStep('done');
    } catch (err) {
      setRegError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setRegLoading(false);
    }
  }

  async function handleWaiverSubmit(e) {
    e.preventDefault();
    setRegLoading(true);
    setRegError('');
    try {
      await eventsApi.register(eventId, {});
      await eventsApi.signWaiver(eventId, {
        waiver_signed: waiverAccepted,
        terms_accepted: termsAccepted
      });
      const res = await eventsApi.getMyRegistration(eventId);
      setMyRegistration(res.data.data);
      setRegStep('done');
    } catch (err) {
      setRegError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setRegLoading(false);
    }
  }

  return (
    <div className="page">
      <div className="page-content">
        {/* Back link */}
        <Link to="/events" style={{ color: 'var(--amber)', fontSize: '0.85rem', textDecoration: 'none', display: 'inline-block', marginBottom: 16 }}>
          &larr; Back to All Events
        </Link>

        {/* Hero section */}
        <div className="card" style={{ padding: '32px 28px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.8rem' }}>{event.name}</h1>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: 8 }}>
                {event.location || 'Location TBD'}
              </div>
              {event.organizer_name && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 4 }}>
                  Organized by {event.organizer_name}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
              <span className={`badge badge-${event.status}`} style={{ fontSize: '0.85rem', padding: '4px 14px' }}>
                {eventStatusLabel(event.status)}
              </span>
              {(isCompleted || isLive) && (
                <a
                  href={`/events/${eventId}/scoreboard`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-sm"
                  style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                >
                  {isCompleted ? 'View Final Scoreboard' : 'View Live Scoreboard'}
                </a>
              )}
              {canRegister && !myRegistration && (
                <button className="btn btn-primary btn-sm" onClick={handleRegister} disabled={regLoading} style={{ whiteSpace: 'nowrap' }}>
                  {regLoading ? 'Registering...' : 'Register for Event'}
                </button>
              )}
              {canRegister && myRegistration && (
                <span style={{ fontSize: '0.82rem', color: myRegistration.registration_status === 'confirmed' ? 'var(--green, #4caf50)' : 'var(--amber)', fontWeight: 600 }}>
                  {myRegistration.registration_status === 'confirmed' ? 'Registered' : 'Registration Pending Approval'}
                </span>
              )}
            </div>
          </div>

          {/* Date range */}
          <div style={{ marginTop: 20, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Start</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{startDate}</div>
            </div>
            <div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>End</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{endDate}</div>
            </div>
          </div>
        </div>

        {/* Details grid */}
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          <div className="card" style={{ padding: 20 }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Format</div>
            <div style={{ fontSize: '1.2rem', fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--amber)', textTransform: 'capitalize' }}>
              {event.event_type || 'Team'}
            </div>
          </div>
          <div className="card" style={{ padding: 20 }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Tournaments</div>
            <div style={{ fontSize: '1.2rem', fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--blue)' }}>
              {tournaments.length}
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {event.featured_image_url && (
          <div style={{ marginBottom: 20 }}>
            <img src={assetUrl(event.featured_image_url)} alt={event.name} style={{ width: '100%', maxHeight: 900, objectFit: 'contain', borderRadius: 10 }} />
          </div>
        )}

        {/* Description */}
        {event.description && (
          <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
            <div className="card-header" style={{ paddingLeft: 0, paddingRight: 0 }}>
              <h3>About This Event</h3>
            </div>
            <div
              style={{ color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}
              dangerouslySetInnerHTML={{ __html: (() => {
                DOMPurify.addHook('afterSanitizeAttributes', node => {
                  if (node.tagName === 'A') { node.setAttribute('target', '_blank'); node.setAttribute('rel', 'noopener noreferrer'); }
                });
                const clean = DOMPurify.sanitize(event.description, { ADD_ATTR: ['target'] });
                DOMPurify.removeAllHooks();
                return clean;
              })() }}
            />
          </div>
        )}

        {/* Tournaments list */}
        {tournaments.length > 0 && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <h3>Tournaments</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {tournaments.map(t => (
                <div key={t.id} style={{
                  padding: '12px 16px', background: 'var(--surface)', borderRadius: 8,
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{t.name}</div>
                  {t.target_species && (
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: 2 }}>
                      Species: {t.target_species}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Waiver */}
        {event.waiver_text && (
          <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
            <div className="card-header" style={{ paddingLeft: 0, paddingRight: 0 }}>
              <h3>Waiver</h3>
            </div>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.85rem' }}>
              {event.waiver_text}
            </p>
          </div>
        )}

        {/* Terms & Conditions */}
        {event.terms_and_conditions && (
          <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
            <div className="card-header" style={{ paddingLeft: 0, paddingRight: 0 }}>
              <h3>Terms & Conditions</h3>
            </div>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.85rem' }}>
              {event.terms_and_conditions}
            </p>
          </div>
        )}

        {/* Registration CTA */}
        {canRegister && !myRegistration && regStep === 'idle' && (
          <div className="card" style={{ padding: '28px 24px', marginBottom: 20, textAlign: 'center' }}>
            {regError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{regError}</div>}
            <h3 style={{ marginBottom: 8 }}>Ready to join?</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 20, fontSize: '0.9rem' }}>
              Register for this event to secure your spot.
            </p>
            <button className="btn btn-primary" onClick={handleRegister} disabled={regLoading}>
              {regLoading ? 'Registering...' : 'Register for Event'}
            </button>
          </div>
        )}

        {/* Waiver / Terms acceptance step */}
        {canRegister && !myRegistration && regStep === 'waiver' && (
          <div className="card" style={{ padding: '28px 24px', marginBottom: 20 }}>
            <h3 style={{ marginBottom: 16 }}>Accept to Complete Registration</h3>
            {regError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{regError}</div>}
            <form onSubmit={handleWaiverSubmit}>
              {event.waiver_text && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input type="checkbox" checked={waiverAccepted} onChange={e => setWaiverAccepted(e.target.checked)} required style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      I have read and accept the <strong style={{ color: 'var(--text-primary)' }}>Waiver</strong> above.
                    </span>
                  </label>
                </div>
              )}
              {event.terms_and_conditions && (
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} required style={{ marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      I agree to the <strong style={{ color: 'var(--text-primary)' }}>Terms & Conditions</strong> above.
                    </span>
                  </label>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="btn btn-primary" disabled={regLoading}>
                  {regLoading ? 'Submitting...' : 'Confirm Registration'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setRegStep('idle')}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Registration success / status */}
        {(regStep === 'done' || (myRegistration && canRegister)) && (
          <div className="card" style={{ padding: '24px', marginBottom: 20, textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, color: myRegistration?.registration_status === 'confirmed' ? 'var(--green, #4caf50)' : 'var(--amber)', marginBottom: 8 }}>
              {myRegistration?.registration_status === 'confirmed' ? 'You are registered!' : 'Registration submitted — pending organizer approval'}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: 0 }}>
              {myRegistration?.registration_status === 'confirmed'
                ? 'Your spot is confirmed. See you at the event!'
                : 'The organizer will review your registration and confirm your spot.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
